const STORAGE_KEY = 'guest_sessions';
const ACTIVE_KEY = 'active_session_id';

export interface StoredSession {
  sessionId: string;
  // The secret session token — the resume credential (W4). Presented to
  // /guest/session/activate as proof-of-possession. Legacy entries persisted
  // before W4 may lack this and are treated as non-resumable.
  sessionToken?: string;
  spaceId: string;
  spaceName: string;
  threadId: string | null;
  // Access scope of the underlying guest session ('thread' | 'space'). Used to
  // key the resume list so distinct stories on a shared device don't clobber
  // each other (W7).
  accessScope?: string;
  displayName: string;
  expiresAt: string;
}

function readSessions(): StoredSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const sessions: StoredSession[] = JSON.parse(raw);
    // Filter out expired sessions
    const now = new Date().toISOString();
    return sessions.filter(s => s.expiresAt > now);
  } catch {
    return [];
  }
}

function writeSessions(sessions: StoredSession[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
}

export function addSession(session: StoredSession): void {
  const sessions = readSessions();
  // Dedupe scoped to the invite granularity so distinct people/stories on a
  // shared device don't clobber each other (W7):
  //   - thread-scoped → replace only an existing entry for the SAME story.
  //   - space-scoped  → replace an existing space-scoped entry for the same space.
  const isThreadScoped = session.accessScope === 'thread' && !!session.threadId;
  const filtered = sessions.filter(s => {
    if (isThreadScoped) {
      return !(s.accessScope === 'thread' && s.threadId === session.threadId);
    }
    return !(s.accessScope !== 'thread' && s.spaceId === session.spaceId);
  });
  filtered.push(session);
  writeSessions(filtered);
  // Set as active
  localStorage.setItem(ACTIVE_KEY, session.sessionId);
}

/**
 * Update a stored session's expiry after the server renewed it.
 *
 * Guest sessions renew server-side as the guest keeps showing up, so the
 * expiresAt captured at join time goes stale. readSessions() drops entries by
 * that snapshot, which would hide a session the server considers perfectly
 * live — so refresh it rather than letting the local copy decide.
 */
export function updateSessionExpiry(sessionId: string, expiresAt: string): void {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return;
  try {
    const sessions: StoredSession[] = JSON.parse(raw);
    const next = sessions.map(s => (s.sessionId === sessionId ? { ...s, expiresAt } : s));
    writeSessions(next);
  } catch {
    // Corrupt store — nothing to refresh.
  }
}

export function removeSession(sessionId: string): void {
  const sessions = readSessions();
  writeSessions(sessions.filter(s => s.sessionId !== sessionId));
  // Clear active if it was this session
  if (localStorage.getItem(ACTIVE_KEY) === sessionId) {
    localStorage.removeItem(ACTIVE_KEY);
  }
}

export function getSessions(): StoredSession[] {
  return readSessions();
}

export function getActiveSessionId(): string | null {
  return localStorage.getItem(ACTIVE_KEY);
}

export function setActiveSessionId(sessionId: string): void {
  localStorage.setItem(ACTIVE_KEY, sessionId);
}

export function getActiveSession(): StoredSession | null {
  const activeId = getActiveSessionId();
  if (!activeId) return null;
  const sessions = readSessions();
  return sessions.find(s => s.sessionId === activeId) ?? null;
}

export function clearAllSessions(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(ACTIVE_KEY);
}
